// src/components/common/ModalitySelector.js
// Reusable modality picker used in onboarding (ModalitiesScreen) and profile settings.
// Props:
//   selectedModalities — [{ id, name, parentId, parentName }]
//   customModalities   — [{ name, parentId, parentName, addedAt }]
//   onChange(selected, custom) — fires on every change
//   maxHeight — optional CSS string e.g. '500px' to constrain scroll area

import React, { useState, useRef, useCallback } from 'react';
import { Search, ChevronDown, ChevronUp, X, Plus, Check } from 'lucide-react';
import {
  MODALITY_TAXONOMY,
  PARENT_CATEGORIES,
  searchModalities,
} from '../../data/modalityTaxonomy';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Capitalize first letter of every word. */
const toTitleCase = (str) =>
  str.replace(/\b\w/g, (c) => c.toUpperCase());

// ─── Static style objects ─────────────────────────────────────────────────────

const S = {
  // Outer wrapper
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    fontFamily: 'inherit',
  },

  // Search
  searchWrap: { position: 'relative' },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9CA3AF',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
  },
  searchInput: (focused) => ({
    width: '100%',
    padding: '10px 14px 10px 40px',
    fontSize: '14px',
    borderRadius: '8px',
    border: focused ? '1.5px solid #7c3aed' : '1px solid #E5E7EB',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    backgroundColor: 'white',
    transition: 'border-color 0.15s ease',
  }),

  // Pills row
  pillsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 10px 5px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '500',
    backgroundColor: '#7c3aed',
    color: 'white',
    lineHeight: 1.2,
    userSelect: 'none',
  },
  customPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 10px 5px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '500',
    border: '1.5px dashed #7c3aed',
    color: '#7c3aed',
    backgroundColor: '#F5F3FF',
    lineHeight: 1.2,
    userSelect: 'none',
  },
  pillX: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: '0',
    color: 'inherit',
    opacity: 0.75,
    lineHeight: 1,
    flexShrink: 0,
  },

  // Search results
  resultsList: {
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: 'white',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  resultsHeader: {
    padding: '8px 14px',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: '#9CA3AF',
    borderBottom: '1px solid #F3F4F6',
    backgroundColor: '#FAFAFA',
  },
  resultItem: (isSelected, isHovered) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    cursor: 'pointer',
    backgroundColor: isSelected ? '#F5F3FF' : isHovered ? '#FAFAFA' : 'white',
    borderBottom: '1px solid #F9FAFB',
    transition: 'background-color 0.1s ease',
    minHeight: '44px',
  }),
  resultName: (isSelected) => ({
    fontSize: '14px',
    fontWeight: isSelected ? '500' : '400',
    color: isSelected ? '#7c3aed' : '#111827',
  }),
  resultParent: {
    fontSize: '12px',
    color: '#9CA3AF',
    marginLeft: '6px',
  },
  resultCheck: {
    flexShrink: 0,
    color: '#7c3aed',
  },
  noResults: {
    padding: '14px',
    fontSize: '14px',
    color: '#9CA3AF',
    textAlign: 'center',
  },

  // Accordion
  accordionWrap: {
    border: '1px solid #E5E7EB',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  categoryRow: (isExpanded, isHovered) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    cursor: 'pointer',
    backgroundColor: isExpanded ? '#FAFAFA' : isHovered ? '#FAFAFA' : 'white',
    borderBottom: isExpanded ? '1px solid #F3F4F6' : 'none',
    transition: 'background-color 0.15s ease',
    userSelect: 'none',
  }),
  categoryRowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: 0,
  },
  categoryName: (isExpanded) => ({
    fontSize: '14px',
    fontWeight: '500',
    color: isExpanded ? '#7c3aed' : '#111827',
    transition: 'color 0.15s ease',
  }),
  categoryCount: {
    fontSize: '12px',
    color: '#9CA3AF',
    flexShrink: 0,
  },
  categorySelectedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    padding: '0 6px',
    borderRadius: '10px',
    backgroundColor: '#7c3aed',
    color: 'white',
    fontSize: '11px',
    fontWeight: '600',
    flexShrink: 0,
  },
  categoryExpandedBody: {
    padding: '0 16px 14px',
    backgroundColor: '#FAFAFA',
  },
  categoryDescription: {
    fontSize: '13px',
    color: '#6B7280',
    lineHeight: '1.5',
    paddingTop: '10px',
    paddingBottom: '12px',
  },
  // Chip grid uses injected CSS class for responsive columns
  chip: (isSelected, isHovered) => ({
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 10px',
    borderRadius: '8px',
    border: isSelected ? '1.5px solid #7c3aed' : '1px solid #E5E7EB',
    backgroundColor: isSelected ? '#F5F3FF' : isHovered ? '#FAFAFA' : 'white',
    color: isSelected ? '#6D28D9' : '#374151',
    fontSize: '13px',
    fontWeight: isSelected ? '500' : '400',
    cursor: 'pointer',
    textAlign: 'center',
    lineHeight: '1.3',
    transition: 'all 0.15s ease',
    boxSizing: 'border-box',
    userSelect: 'none',
    position: 'relative',
  }),
  chipCheck: {
    position: 'absolute',
    top: '5px',
    right: '5px',
    width: '14px',
    height: '14px',
    backgroundColor: '#7c3aed',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  // Divider between category rows
  rowDivider: {
    height: '1px',
    backgroundColor: '#F3F4F6',
    margin: '0',
  },

  // Custom entry
  customTrigger: (isActive, isHovered) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    cursor: 'pointer',
    border: isActive ? '1.5px solid #7c3aed' : '1px solid #E5E7EB',
    borderRadius: '10px',
    backgroundColor: isActive ? '#F5F3FF' : isHovered ? '#FAFAFA' : 'white',
    transition: 'all 0.15s ease',
    userSelect: 'none',
  }),
  customTriggerDot: (isActive) => ({
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: isActive ? '2px solid #7c3aed' : '2px solid #D1D5DB',
    backgroundColor: isActive ? '#7c3aed' : 'transparent',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
  }),
  customTriggerLabel: (isActive) => ({
    fontSize: '14px',
    color: isActive ? '#7c3aed' : '#6B7280',
    fontWeight: isActive ? '500' : '400',
    transition: 'color 0.15s ease',
  }),
  customForm: {
    border: '1px solid #E5E7EB',
    borderRadius: '10px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    backgroundColor: '#FAFAFA',
  },
  formLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '5px',
  },
  formInput: (focused) => ({
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: focused ? '1.5px solid #7c3aed' : '1px solid #E5E7EB',
    borderRadius: '8px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none',
    backgroundColor: 'white',
    transition: 'border-color 0.15s ease',
  }),
  formSelect: (focused) => ({
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: focused ? '1.5px solid #7c3aed' : '1px solid #E5E7EB',
    borderRadius: '8px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease',
    appearance: 'none',
    WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: '36px',
  }),
  formRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  addBtn: (disabled) => ({
    padding: '9px 20px',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: disabled ? '#D1D5DB' : '#7c3aed',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.15s ease',
    flexShrink: 0,
  }),
  cancelBtn: {
    padding: '9px 14px',
    fontSize: '14px',
    color: '#6B7280',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'color 0.15s ease',
  },
  charCount: {
    fontSize: '12px',
    color: '#9CA3AF',
    textAlign: 'right',
    marginTop: '-6px',
  },
};

// ─── Sub-components (defined at module scope — never inside parent render) ────

/** A single selected pill (standard or custom). */
const Pill = ({ label, onRemove, isCustom }) => (
  <span style={isCustom ? S.customPill : S.pill}>
    {label}
    <span
      role="button"
      tabIndex={0}
      onClick={onRemove}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onRemove(); }}
      style={S.pillX}
      aria-label={`Remove ${label}`}
    >
      <X size={13} strokeWidth={2.5} />
    </span>
  </span>
);

/** A single modality chip inside an expanded category. */
const ModalityChip = ({ modality, selected, onToggle }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      role="checkbox"
      aria-checked={selected}
      tabIndex={0}
      onClick={() => onToggle(modality)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(modality); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={S.chip(selected, hovered)}
    >
      {selected && (
        <span style={S.chipCheck}>
          <Check size={8} strokeWidth={3} color="white" />
        </span>
      )}
      {modality.name}
    </div>
  );
};

/** A single search result row. */
const SearchResultRow = ({ modality, selected, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      role="option"
      aria-selected={selected}
      tabIndex={0}
      onClick={() => onSelect(modality)}
      onKeyDown={(e) => { if (e.key === 'Enter') onSelect(modality); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={S.resultItem(selected, hovered)}
    >
      <span>
        <span style={S.resultName(selected)}>{modality.name}</span>
        <span style={S.resultParent}>{'\u2014'} {modality.parentName}</span>
      </span>
      {selected && <Check size={15} style={S.resultCheck} strokeWidth={2.5} />}
    </div>
  );
};

/** One accordion category row + its expandable chip grid. */
const CategoryAccordion = ({ category, selectedModalities, onToggleModality }) => {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);

  const selectedInCategory = category.modalities.filter((m) =>
    selectedModalities.some((s) => s.id === m.id)
  ).length;

  return (
    <div>
      <div
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded((v) => !v); }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={S.categoryRow(expanded, hovered)}
      >
        <span style={S.categoryRowLeft}>
          {category.icon && (
            <span style={{ fontSize: '16px', lineHeight: 1, flexShrink: 0 }} role="img" aria-hidden="true">
              {category.icon}
            </span>
          )}
          <span style={S.categoryName(expanded)}>{category.name}</span>
          <span style={S.categoryCount}>({category.modalities.length})</span>
          {selectedInCategory > 0 && (
            <span style={S.categorySelectedBadge}>{selectedInCategory}</span>
          )}
        </span>
        {expanded
          ? <ChevronUp size={16} color="#9CA3AF" />
          : <ChevronDown size={16} color="#9CA3AF" />
        }
      </div>

      {expanded && (
        <div style={S.categoryExpandedBody}>
          <p style={S.categoryDescription}>{category.description}</p>
          <div className="df-ms-chip-grid">
            {category.modalities.map((modality) => (
              <ModalityChip
                key={modality.id}
                modality={modality}
                selected={selectedModalities.some((s) => s.id === modality.id)}
                onToggle={onToggleModality}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const ModalitySelector = ({
  selectedModalities = [],
  customModalities = [],
  onChange,
  maxHeight,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customParentId, setCustomParentId] = useState('');
  const [customNameFocused, setCustomNameFocused] = useState(false);
  const [customSelectFocused, setCustomSelectFocused] = useState(false);
  const [customTriggerHovered, setCustomTriggerHovered] = useState(false);
  const [addBtnHovered, setAddBtnHovered] = useState(false);
  const searchRef = useRef(null);

  // Toggle a standard modality
  const toggleModality = useCallback((modality) => {
    const already = selectedModalities.some((m) => m.id === modality.id);
    if (already) {
      onChange(selectedModalities.filter((m) => m.id !== modality.id), customModalities);
    } else {
      onChange([...selectedModalities, modality], customModalities);
    }
  }, [selectedModalities, customModalities, onChange]);

  // Remove a standard modality pill
  const removeModality = useCallback((id) => {
    onChange(selectedModalities.filter((m) => m.id !== id), customModalities);
  }, [selectedModalities, customModalities, onChange]);

  // Remove a custom modality pill
  const removeCustom = useCallback((idx) => {
    onChange(selectedModalities, customModalities.filter((_, i) => i !== idx));
  }, [selectedModalities, customModalities, onChange]);

  // Search result click: select + clear query
  const handleResultClick = useCallback((modality) => {
    toggleModality(modality);
    setSearchQuery('');
    searchRef.current?.blur();
  }, [toggleModality]);

  // Add custom entry
  const handleAddCustom = () => {
    const trimmed = customName.trim();
    if (trimmed.length < 2) return;
    const parent = PARENT_CATEGORIES.find((p) => p.id === customParentId);
    onChange(selectedModalities, [
      ...customModalities,
      {
        name: toTitleCase(trimmed),
        parentId: customParentId || '',
        parentName: parent?.name || '',
        addedAt: new Date().toISOString(),
      },
    ]);
    setCustomName('');
    setCustomParentId('');
    setShowCustomForm(false);
  };

  const searchResults = searchQuery.trim().length > 0 ? searchModalities(searchQuery) : [];
  const showResults = searchQuery.trim().length > 0;
  const totalSelected = selectedModalities.length + customModalities.length;
  const canAdd = customName.trim().length >= 2;

  const scrollableStyle = maxHeight
    ? { overflowY: 'auto', maxHeight, paddingRight: '2px' }
    : {};

  return (
    <>
      {/* Injected responsive CSS for chip grid — scoped with df-ms- prefix */}
      <style>{`
        .df-ms-chip-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }
        @media (max-width: 640px) {
          .df-ms-chip-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 360px) {
          .df-ms-chip-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div style={S.root}>
        {/* ── 1. Search bar ────────────────────────────────────────────── */}
        <div style={S.searchWrap}>
          <span style={S.searchIcon}>
            <Search size={16} />
          </span>
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder="Search modalities..."
            style={S.searchInput(searchFocused)}
            aria-label="Search modalities"
            autoComplete="off"
          />
        </div>

        {/* ── 2. Selected pills ────────────────────────────────────────── */}
        {totalSelected > 0 && (
          <div>
            <div style={S.pillsWrap} role="list" aria-label="Selected modalities">
              {selectedModalities.map((m) => (
                <Pill
                  key={m.id}
                  label={m.name}
                  onRemove={() => removeModality(m.id)}
                  isCustom={false}
                />
              ))}
              {customModalities.map((c, i) => (
                <Pill
                  key={`custom-${i}`}
                  label={c.name}
                  onRemove={() => removeCustom(i)}
                  isCustom={true}
                />
              ))}
            </div>
            {selectedModalities.length > 5 && (
              <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
                {selectedModalities.length} modalities selected — your profile will highlight the first 3.
              </p>
            )}
          </div>
        )}

        {/* ── 3. Search results OR accordion ──────────────────────────── */}
        <div style={scrollableStyle}>
          {showResults ? (
            /* Search results list */
            <div style={S.resultsList} role="listbox" aria-label="Search results">
              <div style={S.resultsHeader}>
                Search results for &ldquo;{searchQuery}&rdquo;
              </div>
              {searchResults.length === 0 ? (
                <div style={S.noResults}>No modalities found</div>
              ) : (
                searchResults.map((m) => (
                  <SearchResultRow
                    key={m.id}
                    modality={m}
                    selected={selectedModalities.some((s) => s.id === m.id)}
                    onSelect={handleResultClick}
                  />
                ))
              )}
            </div>
          ) : (
            /* Accordion categories */
            <div style={S.accordionWrap}>
              {MODALITY_TAXONOMY.map((category, idx) => (
                <React.Fragment key={category.id}>
                  {idx > 0 && <div style={S.rowDivider} />}
                  <CategoryAccordion
                    category={category}
                    selectedModalities={selectedModalities}
                    onToggleModality={toggleModality}
                  />
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* ── 4. "My modality isn't listed" ───────────────────────────── */}
        <div>
          {!showCustomForm ? (
            <div
              role="button"
              tabIndex={0}
              onClick={() => setShowCustomForm(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowCustomForm(true); }}
              onMouseEnter={() => setCustomTriggerHovered(true)}
              onMouseLeave={() => setCustomTriggerHovered(false)}
              style={S.customTrigger(false, customTriggerHovered)}
              aria-expanded={false}
            >
              <span style={S.customTriggerDot(false)}>
                <Plus size={10} color="white" />
              </span>
              <span style={S.customTriggerLabel(false)}>
                My modality isn&rsquo;t listed
              </span>
            </div>
          ) : (
            <div style={S.customForm}>
              <div>
                <label style={S.formLabel} htmlFor="custom-modality-name">
                  What do you practice?
                </label>
                <input
                  id="custom-modality-name"
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  onBlur={() => {
                    setCustomNameFocused(false);
                    if (customName.trim()) setCustomName(toTitleCase(customName));
                  }}
                  onFocus={() => setCustomNameFocused(true)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && canAdd) handleAddCustom(); }}
                  placeholder="e.g. Akashic Art Channeling"
                  maxLength={80}
                  style={S.formInput(customNameFocused)}
                  autoComplete="off"
                />
                <div style={S.charCount}>{customName.length}/80</div>
              </div>

              <div>
                <label style={S.formLabel} htmlFor="custom-modality-parent">
                  Which category fits best?
                </label>
                <select
                  id="custom-modality-parent"
                  value={customParentId}
                  onChange={(e) => setCustomParentId(e.target.value)}
                  onFocus={() => setCustomSelectFocused(true)}
                  onBlur={() => setCustomSelectFocused(false)}
                  style={S.formSelect(customSelectFocused)}
                >
                  <option value="">Select a category (optional)</option>
                  {PARENT_CATEGORIES.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div style={S.formRow}>
                <button
                  style={S.cancelBtn}
                  onClick={() => {
                    setShowCustomForm(false);
                    setCustomName('');
                    setCustomParentId('');
                  }}
                >
                  Cancel
                </button>
                <button
                  style={{
                    ...S.addBtn(!canAdd),
                    ...(addBtnHovered && canAdd ? { backgroundColor: '#6D28D9' } : {}),
                  }}
                  onClick={handleAddCustom}
                  onMouseEnter={() => setAddBtnHovered(true)}
                  onMouseLeave={() => setAddBtnHovered(false)}
                  disabled={!canAdd}
                  aria-disabled={!canAdd}
                >
                  Add modality
                </button>
              </div>

              {customName.trim().length > 0 && customName.trim().length < 2 && (
                <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '-4px' }}>
                  Minimum 2 characters required
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ModalitySelector;
